import { type FetchSurveysData, type FetchSurveysParams, type Survey, type SurveyUser } from '@capsicum/common';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { surveyApi } from '../api';

/**
 *  Returns open surveys for partner carousel
 */
export const useCarouselSurveysQuery = (
	partnerId: string,
	params?: FetchSurveysParams,
	options?: Omit<UseQueryOptions<FetchSurveysData, unknown, Array<SurveyUser>>, 'queryFn' | 'queryKey'>
) => {
	const { data, error, isLoading, refetch } = useQuery<FetchSurveysData, unknown, Array<SurveyUser>>({
		queryFn: async () =>
			(await surveyApi).fetchSurveys({
				'details[]': ['surveyTranslations'],
				direction: 'asc',
				limit: 15,
				orderBy: 'closeAt',
				page: 1,
				'partnerIds[]': [partnerId],
				'statuses[]': ['opened', 'scheduled'],
				'types[]': ['engagement', 'integrity'],
				...params
			}),
		queryKey: ['surveys-carousel-open', partnerId, params],
		select: res => res?.data || [],
		...options
	});

	return { data, error, isLoading, refetch };
};

/**
 *  Returns closed surveys for partner carousel
 */
export const useClosedCarouselSurveysQuery = (
	partnerId: string,
	params?: FetchSurveysParams,
	options?: Omit<UseQueryOptions<FetchSurveysData, unknown, Array<Survey>>, 'queryFn' | 'queryKey'>
) => {
	const { data, error, isLoading, refetch } = useQuery<FetchSurveysData, unknown, Array<SurveyUser>>({
		queryFn: async () =>
			(await surveyApi).fetchSurveys({
				'details[]': ['surveyTranslations'],
				direction: 'desc',
				limit: 4,
				orderBy: 'closeAt',
				page: 1,
				'partnerIds[]': [partnerId],
				'statuses[]': ['closed'],
				'types[]': ['engagement', 'integrity'],
				...params
			}),
		queryKey: ['surveys-carousel-closed', partnerId, params],
		select: res => res.data || [],
		...options
	});

	return { data, error, isLoading, refetch };
};
